import { useEffect, useMemo } from 'react'
import useConnectionsToolkitSyncTick from '@/hooks/useConnectionsToolkitSyncTick'
import { resolveConnectionRow } from '@/hooks/resolveConnectionRow'
import {
  MAIL_TOOLKIT_SLUGS,
  readConnectedToolkits,
  readDismissedConnectionIds,
  readEstablishedConnectionAccountRowsFromSession,
  toolkitConnectionRowId,
} from '@/services/connectionsSession'

/**
 * Session-backed toolkit connections for Settings → Connections.
 *
 * @returns {{ connections: ReturnType<typeof resolveConnectionRow>[], refetch: () => void }}
 */
export default function useSettingsConnections() {
  const sessionTick = useConnectionsToolkitSyncTick()

  useEffect(() => {
    void sessionTick
  }, [sessionTick])

  const connections = useMemo(() => {
    void sessionTick
    const dismissed = new Set(readDismissedConnectionIds())
    const sessionRows = readEstablishedConnectionAccountRowsFromSession()

    const byId = new Map()
    for (const row of sessionRows) {
      const id = String(row?.id ?? '').trim()
      if (!id || dismissed.has(id)) continue
      byId.set(id, row)
    }

    for (const slug of readConnectedToolkits()) {
      if (MAIL_TOOLKIT_SLUGS.has(slug)) {
        const hasMailRow = Array.from(byId.values()).some(
          (r) => String(r.toolkitSlug || '').toLowerCase() === slug,
        )
        if (hasMailRow) continue
      }
      const id = toolkitConnectionRowId(slug)
      if (dismissed.has(id) || byId.has(id)) continue
      byId.set(id, { id, toolkitSlug: slug, email: '', summary: '' })
    }

    return Array.from(byId.values()).map(resolveConnectionRow)
  }, [sessionTick])

  const refetch = () => {}

  return { connections, refetch }
}
